import { Skeleton } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceVariables } from 'src/constants/deviceConstants';
import { Device } from 'src/store/models/Device';
import { formatTime, getDayString } from 'src/utils/dateFormatter';
import { addUnit } from 'src/utils/deviceUtils';

import classes from '../HeatingApplianceDetails.module.scss';

export interface ServiceInfoProps {
  loading?: boolean;
  deviceDetails: Device;
}

const ServiceInfo: FC<ServiceInfoProps> = ({
  loading = false,
  deviceDetails,
}) => {
  const { t } = useTranslation();

  const lineData = (label: string, value: number | string) => (
    <p>
      {`${label}: `}
      <span>{value ?? '/'}</span>
    </p>
  );

  const statistics = deviceDetails?.controller?.statistic;
  const variables = deviceDetails?.controller?.diagnostic?.variables;

  const time = formatTime(
    variables?.[DeviceVariables.TIME_HOUR]?.value,
    variables?.[DeviceVariables.TIME_MINUTES]?.value,
    variables?.[DeviceVariables.TIME_SECONDS]?.value
  );

  return (
    <>
      {!loading ? (
        <>
          <h5>{t('device:service_info')}</h5>
          <h6>{t('device:statistics')}</h6>
          {lineData(t('device:igniter_starts'), statistics?.igniterStarts)}
          {lineData(t('device:up_time'), addUnit(statistics?.uptime, 'h'))}
          {lineData(
            t('device:total_heating_hours'),
            addUnit(statistics?.heatingTime, 'h')
          )}
          {lineData(t('device:over_temp_errors'), statistics?.overheatings)}
          {lineData(t('device:missed_firings'), statistics?.misfires)}
          {lineData(
            t('device:total_service_hours'),
            addUnit(statistics?.serviceTime, 'h')
          )}
          <h6>{t('device:diagnostics')}</h6>
          {lineData(
            t('device:fan_1_speed'),
            variables?.[DeviceVariables.FAN_1_SPEED]?.value
          )}
          {lineData(
            t('device:fan_2_speed'),
            variables?.[DeviceVariables.FAN_2_SPEED]?.value
          )}
          {lineData(
            t('device:current_power'),
            variables?.[DeviceVariables.CURRENT_POWER]?.value
          )}
          {lineData(
            t('device:gasses_temp'),
            addUnit(
              variables?.[DeviceVariables.GASSES_TEMPERATURE]?.value,
              '°C'
            )
          )}
          {lineData(t('device:time'), variables?.length ? time : null)}
          {lineData(
            t('device:day_of_the_week'),
            getDayString(variables?.[DeviceVariables.DAY_OF_WEEK]?.value - 1)
          )}
        </>
      ) : (
        <div className={classes.Loading}>
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="rectangular" height={204} />
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="rectangular" height={204} />
        </div>
      )}
    </>
  );
};

export default ServiceInfo;
